import React, { useState, useEffect } from 'react';
import { contactService } from '../../services/api';
import { Mail, Trash2, Loader2, RefreshCw } from 'lucide-react';

function ContactSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({});

  const loadSubmissions = async (pageNum = 1) => {
    setLoading(true);
    try {
      const data = await contactService.index();
      setSubmissions(data.data || data);
      setMeta(data.meta || {});
    } catch (err) {
      console.error('Error loading submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubmissions(page);
  }, [page]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    try {
      await contactService.destroy(id);
      setSubmissions(submissions.filter((s) => s.id !== id));
    } catch (err) {
      console.error('Error deleting submission:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Contact Submissions</h2>
          <p className="text-slate-500 text-sm mt-1">Messages submitted through the contact form on your website.</p>
        </div>
        <button
          onClick={() => loadSubmissions(page)}
          className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-semibold transition"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-red-600" />
        </div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-20 bg-slate-50 rounded-2xl border border-slate-100">
          <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No contact submissions yet.</p>
          <p className="text-slate-400 text-sm mt-1">Form submissions from the contact page will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((s) => (
            <div key={s.id} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-slate-900">{s.name}</h3>
                    <span className="text-xs text-slate-400">{new Date(s.created_at).toLocaleString()}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-3">
                    <a href={`mailto:${s.email}`} className="text-red-600 hover:underline font-medium">{s.email}</a>
                    {s.phone && <span>{s.phone}</span>}
                    {s.subject && <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium">{s.subject}</span>}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">{s.message}</p>
                </div>
                <button
                  onClick={() => handleDelete(s.id)}
                  className="text-slate-400 hover:text-red-600 transition flex-shrink-0"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}

          {meta.last_page > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 hover:bg-slate-200 disabled:opacity-50 transition"
              >
                Previous
              </button>
              <span className="text-sm text-slate-500">Page {page} of {meta.last_page}</span>
              <button
                onClick={() => setPage(Math.min(meta.last_page, page + 1))}
                disabled={page === meta.last_page}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 hover:bg-slate-200 disabled:opacity-50 transition"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ContactSubmissions;
